import { createContext, useContext, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { submitLead } from "@/lib/api";
import { PRODUCTS } from "@/data/content";

const EnquiryContext = createContext(null);
export const useEnquiry = () => useContext(EnquiryContext);

export const EnquiryProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("enquiry");
  const [form, setForm] = useState({ name: "", phone: "", email: "", machinery: "", message: "" });
  const [loading, setLoading] = useState(false);

  const openEnquiry = useCallback((machinery = "", src = "enquiry") => {
    setSource(src);
    setForm((f) => ({ ...f, machinery }));
    setOpen(true);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, email: form.email || null, source });
      toast.success("Enquiry received! Our team will reach out shortly.");
      setForm({ name: "", phone: "", email: "", machinery: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  const isDealer = source === "dealer";

  return (
    <EnquiryContext.Provider value={{ openEnquiry }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-brand-cream border-brand-ink/10" data-testid="enquiry-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight text-brand-ink">
              {isDealer ? "Dealer & Distributor Enquiry" : "Request a Quote"}
            </DialogTitle>
            <DialogDescription className="text-brand-ink/60">
              {isDealer
                ? "Partner with ADK SOLUTIONS. Share your details and we'll connect."
                : "Tell us what you need. Our experts respond within a business day."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-1">
            <div className="space-y-1.5">
              <Label htmlFor="enq-name">Name *</Label>
              <Input id="enq-name" data-testid="enquiry-name" value={form.name} onChange={set("name")} placeholder="Your full name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="enq-phone">Phone *</Label>
                <Input id="enq-phone" data-testid="enquiry-phone" value={form.phone} onChange={set("phone")} placeholder="+91 …" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="enq-email">Email</Label>
                <Input id="enq-email" data-testid="enquiry-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" />
              </div>
            </div>
            {!isDealer && (
              <div className="space-y-1.5">
                <Label>Machinery interested in</Label>
                <Select value={form.machinery} onValueChange={(v) => setForm((f) => ({ ...f, machinery: v }))}>
                  <SelectTrigger data-testid="enquiry-machinery"><SelectValue placeholder="Select a product" /></SelectTrigger>
                  <SelectContent>
                    {PRODUCTS.map((p) => (
                      <SelectItem key={p.slug} value={p.name}>{p.name}</SelectItem>
                    ))}
                    <SelectItem value="Other / Not sure">Other / Not sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="enq-msg">Message</Label>
              <Textarea id="enq-msg" data-testid="enquiry-message" value={form.message} onChange={set("message")} placeholder={isDealer ? "Your region, business background…" : "Farm size, crop, any questions…"} rows={3} />
            </div>
            <Button type="submit" disabled={loading} data-testid="enquiry-submit" className="w-full rounded-full bg-brand-green text-white hover:bg-brand-greenDark h-11">
              {loading ? <Loader2 className="animate-spin" /> : <Send className="size-4" />}
              {loading ? "Sending…" : "Submit Enquiry"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </EnquiryContext.Provider>
  );
};
